import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import communityAPI from '../../../api/community';
import { InlineSpinner } from '../../../componentes/LoadingIndicator';
import PasswordStrengthIndicator from '../PasswordStrengthIndicator';
import LegalDocumentModal from '../LegalDocumentModal';
import PhoneInput, { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import Select from 'react-select';
import { getAllCountries } from '../../../utils/countries';
import { message } from 'antd';
import 'react-phone-number-input/style.css';
import './register-inputs.css';

const steps = [
  { number: 1, title: 'Account', subtitle: 'Credentials & security', icon: '👤' },
  { number: 2, title: 'Profile', subtitle: 'Personal details', icon: '📝' },
  { number: 3, title: 'Review', subtitle: 'Final check', icon: '✓' }
];

const highlights = [
  {
    title: 'Stay informed',
    description: 'Access curated security insights, threat reports, and community briefings.'
  },
  {
    title: 'Share expertise',
    description: 'Publish research, join discussions, and earn recognition through community badges.'
  },
  {
    title: 'Expand your network',
    description: 'Connect with trusted practitioners across incident response, privacy, and resilience.'
  }
];

export default function Register() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password2: '',
    bio: '',
    title: '',
    location: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const countryOptions = useMemo(() => {
    return getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
      phoneCode: country.phonecode
    }));
  }, []);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityTouched, setCityTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [locationHydrated, setLocationHydrated] = useState(false);

  useEffect(() => {
    if (!phoneTouched) return;
    setFieldErrors((prev) => {
      const hasError = Boolean(formData.phone) && !isValidPhoneNumber(formData.phone);
      const existing = prev.phone;
      if (hasError) {
        const message = 'Enter a valid international phone number.';
        if (existing === message) {
          return prev;
        }
        return { ...prev, phone: message };
      }
      if (!existing) {
        return prev;
      }
      const { phone, ...rest } = prev;
      return rest;
    });
  }, [formData.phone, phoneTouched]);

  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  useEffect(() => {
    if (locationHydrated) return;

    if (!formData.location) {
      setLocationHydrated(true);
      return;
    }

    const segments = formData.location.split(',').map((part) => part.trim()).filter(Boolean);
    if (!segments.length) {
      setLocationHydrated(true);
      return;
    }

    const maybeCountryName = segments[segments.length - 1];
    const maybeCityName = segments.slice(0, -1).join(', ');

    const matchedCountry = countryOptions.find((option) => option.label === maybeCountryName);
    if (matchedCountry) {
      setSelectedCountry(matchedCountry);
    }

    if (matchedCountry && maybeCityName) {
      const cityList = getCitiesOfCountry(matchedCountry.value) || [];
      const matchedCity = cityList.find((city) => city.name === maybeCityName);
      if (matchedCity) {
        setSelectedCity({ value: matchedCity.name, label: matchedCity.name });
      } else {
        setSelectedCity({ value: maybeCityName, label: maybeCityName });
      }
    }

    setLocationHydrated(true);
  }, [formData.location, countryOptions, locationHydrated]);

  useEffect(() => {
    if (!locationHydrated) return;

    if (!selectedCountry) {
      clearFieldError('country');
      clearFieldError('city');
      if (cityTouched) {
        setCityTouched(false);
      }
      if (selectedCity) setSelectedCity(null);
      if (formData.location) {
        setFormData((prev) => prev.location ? { ...prev, location: '' } : prev);
      }
      return;
    }

    if (selectedCountry && selectedCity) {
      const nextLocation = `${selectedCity.label}, ${selectedCountry.label}`;
      if (formData.location !== nextLocation) {
        setFormData((prev) => ({ ...prev, location: nextLocation }));
      }
      return;
    }

    if (!selectedCity && formData.location) {
      setFormData((prev) => ({ ...prev, location: '' }));
    }
  }, [selectedCountry, selectedCity, formData.location, locationHydrated, clearFieldError, cityTouched]);

  const countryHelperMessage = fieldErrors.country || '';
  const cityHelperMessage = fieldErrors.city || (selectedCountry && cityTouched && !selectedCity ? 'Please enter your city.' : '');
  const phoneHelperMessage = fieldErrors.phone || (phoneTouched && formData.phone && !isValidPhoneNumber(formData.phone) ? 'Enter a valid international phone number.' : '');
  const locationHasError = Boolean(countryHelperMessage || cityHelperMessage);
  const phoneHasError = Boolean(phoneHelperMessage);

  const progressPercent = steps.length > 1 ? ((currentStep - 1) / (steps.length - 1)) * 100 : 0;

  const validateStep = (step) => {
    setError('');
    const newFieldErrors = {};
    let stepFieldNames = [];

    if (step === 1) {
      stepFieldNames = ['username', 'email', 'password', 'password2'];
      const username = formData.username.trim();
      const email = formData.email.trim();
      const password = formData.password;
      const password2 = formData.password2;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!username) {
        newFieldErrors.username = 'Username is required';
      }

      if (!email) {
        newFieldErrors.email = 'Email is required';
      } else if (!emailRegex.test(email)) {
        newFieldErrors.email = 'Please enter a valid email';
      }

      if (!password) {
        newFieldErrors.password = 'Password is required';
      } else if (password.length < 8) {
        newFieldErrors.password = 'Password must be at least 8 characters';
      } else if (!/[A-Z]/.test(password)) {
        newFieldErrors.password = 'Password must contain at least one uppercase letter';
      } else if (!/[a-z]/.test(password)) {
        newFieldErrors.password = 'Password must contain at least one lowercase letter';
      } else if (!/[0-9]/.test(password)) {
        newFieldErrors.password = 'Password must contain at least one number';
      }

      if (!password2) {
        newFieldErrors.password2 = 'Please confirm your password';
      } else if (password && password2 !== password) {
        newFieldErrors.password2 = 'Passwords do not match';
      }
    } else if (step === 2) {
      stepFieldNames = ['country', 'city', 'phone'];
      setCityTouched(true);
      setPhoneTouched(true);

      if (selectedCity && !selectedCountry) {
        newFieldErrors.country = 'Please select your country';
      }
      if (selectedCountry && !selectedCity) {
        newFieldErrors.city = 'Please enter your city';
      }
      if (formData.phone && !isValidPhoneNumber(formData.phone)) {
        newFieldErrors.phone = 'Enter a valid international phone number.';
      }
    }

    if (stepFieldNames.length) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        stepFieldNames.forEach((field) => {
          if (!newFieldErrors[field]) {
            delete updated[field];
          }
        });
        if (Object.keys(newFieldErrors).length) {
          return { ...updated, ...newFieldErrors };
        }
        return updated;
      });
    }

    if (Object.keys(newFieldErrors).length) {
      return false;
    }

    if (step === 3 && !agreedToTerms) {
      setError('You must agree to the Terms and Privacy Policy');
      return false;
    }

    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const [avatarFile, setAvatarFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [coverPreview, setCoverPreview] = useState('');

  const handleImageChange = (event, type) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    // Frontend validation to prevent backend rejection
    if (!file.type.startsWith('image/')) {
      message.error('Please select a valid image file.');
      event.target.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) { // 2MB max
      message.error('Image is too large. Please upload an image smaller than 2MB.');
      event.target.value = '';
      return;
    }

    // Additional frontend dimension checking to match backend's PROFILE_MEDIA_MAX_PIXELS (12,000,000)
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const pixels = img.width * img.height;
      if (pixels > 12000000) {
        message.error('Image dimensions are too large. Please upload a smaller image.');
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        if (type === 'avatar') {
          setAvatarPreview(result);
        } else {
          setCoverPreview(result);
        }
      };
      reader.readAsDataURL(file);

      if (type === 'avatar') {
        setAvatarFile(file);
      } else {
        setCoverFile(file);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      message.error('Invalid image file.');
      event.target.value = '';
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    setError('');
    setIsSubmitting(true);

    try {
      // First register the user with profile data
      const registrationData = {
        username: formData.username,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        bio: formData.bio,
        title: formData.title,
        location: formData.location,
        phone: formData.phone,
        terms_accepted: agreedToTerms
      };

      // Remove empty fields to avoid backend issues
      Object.keys(registrationData).forEach(key => {
        if (registrationData[key] === '') {
          delete registrationData[key];
        }
      });

      const response = await communityAPI.auth.register(registrationData);

      // Redirect to OTP verification
      if (response.data.message) {
        navigate('/community/verify-otp', {
          state: {
            email: response.data.email,
            username: response.data.username,
            pendingAvatarDataUrl: avatarPreview || null,
            pendingAvatarName: avatarFile?.name || null,
            pendingCoverDataUrl: coverPreview || null,
            pendingCoverName: coverFile?.name || null
          }
        });
        return;
      }

      if (response.data?.user) {
        navigate('/community', {
          state: { message: 'Account created successfully.' }
        });
        return;
      }

      navigate('/community/login', {
        state: { message: 'Account created successfully. Please sign in.' }
      });
    } catch (err) {
      const backendError = err.response?.data;

      const extractMessage = (detail) => {
        if (!detail) return '';
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail) && detail.length) {
          return extractMessage(detail[0]);
        }
        if (typeof detail === 'object') {
          const priorityKeys = ['error', 'phone', 'email', 'username', 'non_field_errors'];
          for (const key of priorityKeys) {
            if (Object.prototype.hasOwnProperty.call(detail, key)) {
              const message = extractMessage(detail[key]);
              if (message) return message;
            }
          }
          const firstKey = Object.keys(detail)[0];
          if (firstKey) {
            return extractMessage(detail[firstKey]);
          }
        }
        return '';
      };

      const normalizedError = extractMessage(backendError) || err.message || 'Registration failed';
      setError(normalizedError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/5 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="absolute inset-0 opacity-40">
        <div className="absolute -top-32 -left-24 h-64 w-64 rounded-full bg-indigo-300 blur-3xl dark:bg-indigo-900/40" />
        <div className="absolute bottom-10 right-10 h-72 w-72 rounded-full bg-purple-200 blur-3xl dark:bg-purple-900/30" />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-16">
        <div className="grid w-full max-w-6xl gap-6 lg:grid-cols-[320px_1fr]">
          <aside className="hidden rounded-3xl bg-gradient-to-br from-primary via-secondary to-indigo-700 p-10 text-white shadow-2xl lg:flex lg:flex-col lg:justify-between">
            <div>
              <span className="inline-flex items-center rounded-full border border-white/30 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                Cert Community
              </span>
              <h1 className="mt-6 text-3xl font-bold leading-tight">Join a trusted network of cyber defenders</h1>
              <p className="mt-4 text-sm text-indigo-100/90">
                Create your account to collaborate with experts, access curated learning paths, and stay ahead of emerging threats.
              </p>
            </div>
            <ul className="space-y-6">
              {highlights.map((item) => (
                <li key={item.title} className="rounded-2xl bg-white/10 p-5 shadow-inner shadow-indigo-900/30 backdrop-blur">
                  <p className="text-sm font-semibold uppercase tracking-wide text-white/70">{item.title}</p>
                  <p className="mt-2 text-sm text-indigo-100/90">{item.description}</p>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-xs text-indigo-100/80">
              Secure infrastructure, dedicated moderation, and privacy-first policies keep your work protected.
            </p>
          </aside>

          <div className="rounded-3xl border border-white/60 bg-white/90 p-8 shadow-2xl backdrop-blur dark:border-slate-800/60 dark:bg-slate-900/90">
            <div className="flex flex-col gap-4 border-b border-gray-200 pb-6 dark:border-slate-700">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-3xl font-semibold text-gray-900 dark:text-white">Create your profile</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Step {currentStep} of {steps.length}</p>
                </div>
                <div className="hidden rounded-full bg-primary/10 px-4 py-2 text-xs font-semibold tracking-wide text-primary dark:bg-[#3db8d9]/10 dark:text-primary/70 sm:inline-flex">
                  Guided onboarding
                </div>
              </div>
              <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {steps.map((step) => {
                  const isActive = currentStep === step.number;
                  const isComplete = currentStep > step.number;
                  return (
                    <div
                      key={step.number}
                      className={`rounded-2xl border p-4 transition-all ${isActive
                        ? 'border-primary bg-primary/10 shadow-sm dark:border-primary/60/80 dark:bg-[#3db8d9]/10'
                        : isComplete
                          ? 'border-primary/30 bg-primary/10/70 dark:border-primary/40 dark:bg-[#3db8d9]/10'
                          : 'border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-10 w-10 items-center justify-center rounded-full text-lg ${isActive || isComplete
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-md'
                          : 'bg-gray-200 text-gray-600 dark:bg-slate-700 dark:text-gray-200'
                          }`}>
                          {step.icon}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{step.title}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{step.subtitle}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 shadow-sm dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6">
              {currentStep === 1 && (
                <div className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Username *</label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({ ...prev, username: value }));
                          clearFieldError('username');
                        }}
                        className={`mt-2 w-full rounded-xl border border-gray-200 bg-white p-3.5 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20 ${fieldErrors.username ? 'border-red-400 focus:border-red-400 focus:ring-red-300/40 dark:border-red-400/70 dark:focus:ring-red-400/25' : ''
                          }`}
                        placeholder="Choose a unique handle"
                        autoComplete="username"
                        aria-invalid={Boolean(fieldErrors.username)}
                      />
                      {fieldErrors.username && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.username}</p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({ ...prev, email: value }));
                          clearFieldError('email');
                        }}
                        className={`mt-2 w-full rounded-xl border border-gray-200 bg-white p-3.5 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20 ${fieldErrors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-300/40 dark:border-red-400/70 dark:focus:ring-red-400/25' : ''
                          }`}
                        placeholder="you@example.com"
                        autoComplete="email"
                        aria-invalid={Boolean(fieldErrors.email)}
                      />
                      {fieldErrors.email && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.email}</p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Password *</label>
                      <div className="relative mt-2">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, password: value }));
                            clearFieldError('password');
                            if (fieldErrors.password2 && formData.password2 && formData.password2 === value) {
                              clearFieldError('password2');
                            }
                          }}
                          className={`w-full rounded-xl border border-gray-200 bg-white p-3.5 pr-12 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20 ${fieldErrors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-300/40 dark:border-red-400/70 dark:focus:ring-red-400/25' : ''
                            }`}
                          placeholder="Create a strong password"
                          autoComplete="new-password"
                          aria-invalid={Boolean(fieldErrors.password)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary/80"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {fieldErrors.password && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.password}</p>
                      )}
                      <PasswordStrengthIndicator password={formData.password} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Confirm password *</label>
                      <div className="relative mt-2">
                        <input
                          type={showPassword2 ? "text" : "password"}
                          value={formData.password2}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, password2: value }));
                            clearFieldError('password2');
                          }}
                          className={`w-full rounded-xl border border-gray-200 bg-white p-3.5 pr-12 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20 ${fieldErrors.password2 ? 'border-red-400 focus:border-red-400 focus:ring-red-300/40 dark:border-red-400/70 dark:focus:ring-red-400/25' : ''
                            }`}
                          placeholder="Re-enter password"
                          autoComplete="new-password"
                          aria-invalid={Boolean(fieldErrors.password2)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword2(!showPassword2)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary/80"
                        >
                          {showPassword2 ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {fieldErrors.password2 && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.password2}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">First name</label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="mt-2 w-full rounded-xl border border-gray-200 bg-white p-3.5 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20"
                        placeholder="Your First Name"
                        autoComplete="given-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Last name</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="mt-2 w-full rounded-xl border border-gray-200 bg-white p-3.5 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20"
                        placeholder="Your Last Name"
                        autoComplete="family-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Professional title</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="mt-2 w-full rounded-xl border border-gray-200 bg-white p-3.5 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20"
                        placeholder="Security Analyst"
                        autoComplete="organization-title"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Location</label>
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">Choose country then city</span>
                      </div>
                      <div className="mt-2 space-y-3 location-select-group">
                        <Select
                          className={`react-select-container w-full ${countryHelperMessage ? 'has-error' : ''}`}
                          classNamePrefix="react-select"
                          options={countryOptions}
                          value={selectedCountry}
                          onChange={(option) => {
                            setSelectedCountry(option || null);
                            setSelectedCity(null);
                            clearFieldError('country');
                            clearFieldError('city');
                          }}
                          placeholder="Select your country"
                          isClearable
                          isSearchable
                          aria-label="Select your country"
                          getOptionValue={(option) => option.value}
                          noOptionsMessage={() => 'No matches found'}
                        />
                        <input
                          type="text"
                          value={selectedCity?.label || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedCity(value ? { value, label: value } : null);
                            setCityTouched(true);
                            clearFieldError('city');
                          }}
                          onBlur={() => setCityTouched(true)}
                          disabled={!selectedCountry}
                          className={`w-full rounded-xl border border-gray-200 bg-white p-3.5 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 ${cityHelperMessage ? 'border-red-400 focus:border-red-400 focus:ring-red-300/40 dark:border-red-400/70 dark:focus:ring-red-400/25' : ''}`}
                          placeholder={selectedCountry ? 'Enter your city' : 'Select a country first'}
                          aria-invalid={Boolean(cityHelperMessage)}
                        />
                      </div>
                      {locationHasError && (
                        <div className="mt-1 space-y-1 text-xs text-red-600 dark:text-red-400">
                          {countryHelperMessage && <p>{countryHelperMessage}</p>}
                          {cityHelperMessage && <p>{cityHelperMessage}</p>}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Phone</label>
                      <div
                        className={`phone-input-container mt-2 rounded-xl border border-gray-200 bg-white text-gray-900 shadow-sm transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus-within:border-primary/60 dark:focus-within:ring-primary/20 ${phoneHasError ? 'ring-2 ring-red-400/60 border-red-400' : ''
                          }`}
                      >
                        <PhoneInput
                          international
                          defaultCountry="US"
                          country={selectedCountry?.value}
                          value={formData.phone || undefined}
                          onChange={(value) => {
                            setFormData((prev) => ({ ...prev, phone: value || '' }));
                            if (fieldErrors.phone) {
                              clearFieldError('phone');
                            }
                          }}
                          onBlur={() => setPhoneTouched(true)}
                          numberInputProps={{
                            id: 'phone',
                            name: 'phone',
                            autoComplete: 'tel',
                            placeholder: 'Enter phone number',
                            'aria-invalid': phoneHasError
                          }}
                          countrySelectProps={{ 'aria-label': 'Country code' }}
                        />
                      </div>
                      {phoneHasError && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{phoneHelperMessage}</p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Bio</label>
                      <textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        rows={3}
                        className="mt-2 w-full rounded-2xl border border-gray-200 bg-white p-3.5 text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20"
                        placeholder="Share a quick introduction to help others connect with you."
                      />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Profile picture</label>
                      <div className="mt-2 flex h-40 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 text-center transition hover:border-primary dark:border-slate-700 dark:bg-slate-900/60">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(event) => handleImageChange(event, 'avatar')}
                          className="hidden"
                          id="avatar-upload"
                        />
                        <label htmlFor="avatar-upload" className="flex h-full w-full cursor-pointer flex-col items-center justify-center px-6">
                          {avatarPreview ? (
                            <>
                              <div className="flex flex-col items-center">
                                <span className="mb-3 inline-flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-primary/30 bg-white shadow-sm">
                                  <img src={avatarPreview} alt="Avatar preview" className="h-full w-full object-cover" />
                                </span>
                                <p className="text-sm font-medium text-primary dark:text-primary/70">Update profile picture</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG up to 5MB</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-xl shadow-sm dark:bg-slate-800">📷</span>
                              <p className="text-sm font-medium text-primary dark:text-primary/70">Upload profile picture</p>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">PNG, JPG up to 5MB</p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Cover image</label>
                      <div className="mt-2 flex h-40 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 text-center transition hover:border-primary dark:border-slate-700 dark:bg-slate-900/60">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(event) => handleImageChange(event, 'cover')}
                          className="hidden"
                          id="cover-upload"
                        />
                        <label htmlFor="cover-upload" className="flex h-full w-full cursor-pointer flex-col items-center justify-center px-6">
                          {coverPreview ? (
                            <>
                              <div className="flex flex-col items-center">
                                <span className="mb-3 inline-flex h-16 w-28 overflow-hidden rounded-xl border border-primary/30 bg-white shadow-sm">
                                  <img src={coverPreview} alt="Cover preview" className="h-full w-full object-cover" />
                                </span>
                                <p className="text-sm font-medium text-primary dark:text-primary/70">Update cover image</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG up to 5MB</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-xl shadow-sm dark:bg-slate-800">🖼️</span>
                              <p className="text-sm font-medium text-primary dark:text-primary/70">Upload cover image</p>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">PNG, JPG up to 5MB</p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="rounded-3xl border border-gray-200 bg-gradient-to-br from-primary/5 via-white to-secondary/5 p-6 shadow-sm dark:border-slate-700 dark:from-slate-800/50 dark:via-slate-800/80 dark:to-slate-800/50">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Review your information</h3>
                    <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Username</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{formData.username}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Email</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{formData.email}</dd>
                      </div>
                      {(formData.first_name || formData.last_name) && (
                        <div>
                          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Name</dt>
                          <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{`${formData.first_name} ${formData.last_name}`.trim()}</dd>
                        </div>
                      )}
                      {formData.title && (
                        <div>
                          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Title</dt>
                          <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{formData.title}</dd>
                        </div>
                      )}
                      {formData.location && (
                        <div>
                          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Location</dt>
                          <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{formData.location}</dd>
                        </div>
                      )}
                      {formData.phone && (
                        <div>
                          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Phone</dt>
                          <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                            {formatPhoneNumberIntl(formData.phone) || formData.phone}
                          </dd>
                        </div>
                      )}
                      {formData.bio && (
                        <div className="sm:col-span-2">
                          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Bio</dt>
                          <dd className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formData.bio}</dd>
                        </div>
                      )}
                    </dl>
                    {(avatarPreview || coverPreview) && (
                      <div className="mt-6 grid gap-4 sm:grid-cols-2">
                        {avatarPreview && (
                          <div className="rounded-2xl border border-gray-200 bg-white/70 p-4 text-center shadow-sm dark:border-slate-600 dark:bg-slate-800/80">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Profile picture</p>
                            <span className="mt-3 inline-flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-primary/30 bg-white shadow-sm dark:border-primary/40 dark:bg-slate-800">
                              <img src={avatarPreview} alt="Avatar preview" className="h-full w-full object-cover" />
                            </span>
                          </div>
                        )}
                        {coverPreview && (
                          <div className="rounded-2xl border border-gray-200 bg-white/70 p-4 text-center shadow-sm dark:border-slate-600 dark:bg-slate-800/80">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Cover image</p>
                            <span className="mt-3 inline-flex h-16 w-full max-w-[180px] overflow-hidden rounded-xl border border-primary/30 bg-white shadow-sm dark:border-primary/40 dark:bg-slate-800">
                              <img src={coverPreview} alt="Cover preview" className="h-full w-full object-cover" />
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-3xl border border-gray-200 bg-gray-50/70 p-5 dark:border-slate-600 dark:bg-slate-800/60">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={agreedToTerms}
                        onChange={(event) => setAgreedToTerms(event.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-slate-500 dark:bg-slate-700"
                      />
                      <label htmlFor="terms" className="text-sm text-gray-700 dark:text-gray-300">
                        I agree to the{' '}
                        <button type="button" onClick={() => setLegalModal({ isOpen: true, type: 'terms' })} className="font-semibold text-primary hover:underline dark:text-primary/70">
                          Terms of Service
                        </button>{' '}
                        and{' '}
                        <button type="button" onClick={() => setLegalModal({ isOpen: true, type: 'privacy' })} className="font-semibold text-primary hover:underline dark:text-primary/70">
                          Privacy Policy
                        </button>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex-1 rounded-2xl border border-gray-300 bg-white py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-indigo-300 hover:text-primary dark:border-slate-700 dark:bg-slate-800 dark:text-gray-200 dark:hover:border-primary/60 dark:hover:text-primary/70"
                  >
                    Back
                  </button>
                )}
                {currentStep < steps.length ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex-1 rounded-2xl bg-gradient-to-r from-primary to-secondary py-3 text-sm font-semibold text-white shadow-lg transition hover:from-indigo-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={isSubmitting}
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!agreedToTerms || isSubmitting}
                    className="flex-1 rounded-2xl bg-gradient-to-r from-primary to-secondary py-3 text-sm font-semibold text-white shadow-xl transition hover:from-indigo-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <InlineSpinner
                        tone="slate"
                        message="Creating account…"
                        messageClassName="text-white"
                      />
                    ) : (
                      'Create account'
                    )}
                  </button>
                )}
              </div>
            </form>

            <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
              Already have an account?{' '}
              <Link to="/community/login" className="font-semibold text-primary hover:text-indigo-500 dark:text-primary/70">
                Log in
              </Link>
            </div>
          </div>
        </div>
      </div>

      <LegalDocumentModal
        type={legalModal.type}
        isOpen={legalModal.isOpen}
        onClose={() => setLegalModal({ isOpen: false, type: null })}
      />
    </div>
  );
}
